import { apiFetch } from './client';
import { config } from '../config';

export interface Member {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  city: string;
  phone: string;
  street_address: string;
  postal_code: string;
  // National ID. Empty when status='none'. Canonical (12-digit SE,
  // 11-digit NO/FI, 10-digit DK) when status='valid'; raw imported value
  // otherwise. SPA renders a warning indicator when status='needs_review'.
  personal_id?: string;
  personal_id_status: 'none' | 'valid' | 'needs_review';
  // True when the member has at least one guardian link. Drives the
  // expand-chevron column on the Members table.
  has_guardians: boolean;
  // True when the member has a profile photo. Drives the avatar bubble
  // in the Members table — render <img src=memberPhotoURL(...)> when
  // true, initials fallback when false.
  has_photo: boolean;
  // True when this member has no email AND no phone AND no contactable
  // guardian. Drives the orange (warn-tier) triangle on the members table.
  // Severe-tier (red) is driven by personal_id_status; the row picks the
  // worse-of-the-two for display.
  has_contact_issue: boolean;
  // True when the member's linked login has a live device token — the app
  // is installed and push notifications reach them. Drives the phone
  // indicator next to the name on the members table.
  has_push: boolean;
  // True when the active search matched a guardian of this member but not
  // the member's own fields — drives auto-expansion of the guardian panel
  // so the hit isn't hidden behind the chevron. Backend omits the field
  // when false (no search active or member-self matched), so treat the
  // missing-key case as false.
  matched_via_guardian?: boolean;
  created_at: string;
  // Teams this member belongs to, present only when the request passed
  // includeTeams. Drives the team badges in pickers (e.g. the assistant's
  // assign-leader flow). Omitted by the backend when not requested.
  teams?: MemberTeamBadge[];
  // Membership-fee status for the current fiscal year (PR2). Present only when
  // the org has configured a membership-fee invoice type; omitted otherwise so
  // the column renders "—". `paid` is true only when a paid membership-fee
  // invoice covers the member; `status` is the newest matching invoice's status
  // ('' / absent = no invoice at all).
  membership?: MemberMembership;
}

export interface MemberMembership {
  paid: boolean;
  status?: string;
  invoice_number?: string;
  fiscal_year: number;
}

// MemberTeamBadge is the minimal team descriptor for a badge: id, name, color.
export interface MemberTeamBadge {
  id: string;
  name: string;
  color: string;
}

export interface ListMembersResponse {
  members: Member[];
  total: number;
}

export interface ListMembersOptions {
  search?: string;
  limit?: number;
  offset?: number;
  // Sort column id (mirrors the backend's allowlist in
  // internal/services/members/service.go). Unknown values are silently
  // ignored server-side and fall back to the natural last_name + first_name
  // ordering, so a stale `?sort=` value never breaks the page.
  sort?: string;
  order?: 'asc' | 'desc';
  teamIds?: string[];
  personalIdStatuses?: ('none' | 'valid' | 'needs_review')[];
  hasPhoto?: boolean;
  hasEmail?: boolean;
  hasPhone?: boolean;
  hasContactIssue?: boolean;
  isLeader?: boolean;
  createdAfter?: string;  // YYYY-MM-DD
  createdBefore?: string; // YYYY-MM-DD
  // Opts in to including members who are *purely* guardians (i.e. only
  // appear under another member's accordion). Default false — the main
  // members list hides them; the export wants them so a ticked guardian
  // ends up in the sheet.
  includeGuardians?: boolean;
  // Opts into team-awareness: each returned member carries its `teams`, and
  // the free-text search additionally matches team names. Used by pickers
  // that show team badges (assistant assign-leader). The main members table
  // leaves it off.
  includeTeams?: boolean;
  // Billing filter (PR2): restrict to members by their invoice status for a
  // cost type in a fiscal year. costTypeId falls back to the membership-fee
  // type server-side; fiscalYear falls back to the org's current fiscal year.
  costTypeId?: string;
  paymentStatus?: 'paid' | 'unpaid' | 'none';
  fiscalYear?: number;
}

// MemberMonth / MemberHistory mirror internal/services/members' structs: the
// roster curve rebuilt from join (created_at) and departure (deleted_at) dates.
// Total is the live headcount at month end.
export interface MemberMonth {
  month: string; // RFC3339, first instant of the month (UTC)
  joined: number;
  left: number;
  total: number;
}

export interface MemberHistory {
  months: MemberMonth[];
}

export async function getMemberHistory(
  token: string | null,
  slug: string,
  months = 12,
): Promise<MemberHistory> {
  return apiFetch<MemberHistory>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/members/history?months=${months}`,
    { token },
  );
}

export async function listMembers(
  token: string | null,
  slug: string,
  opts: ListMembersOptions = {},
): Promise<ListMembersResponse> {
  const qs = new URLSearchParams();
  if (opts.search) qs.set('search', opts.search);
  if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
  if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
  if (opts.sort) qs.set('sort', opts.sort);
  if (opts.order) qs.set('order', opts.order);
  for (const id of opts.teamIds ?? []) qs.append('team_id', id);
  for (const s of opts.personalIdStatuses ?? []) qs.append('personal_id_status', s);
  if (opts.hasPhoto !== undefined) qs.set('has_photo', String(opts.hasPhoto));
  if (opts.hasEmail !== undefined) qs.set('has_email', String(opts.hasEmail));
  if (opts.hasPhone !== undefined) qs.set('has_phone', String(opts.hasPhone));
  if (opts.hasContactIssue !== undefined) qs.set('has_contact_issue', String(opts.hasContactIssue));
  if (opts.isLeader !== undefined) qs.set('is_leader', String(opts.isLeader));
  if (opts.createdAfter) qs.set('created_after', opts.createdAfter);
  if (opts.createdBefore) qs.set('created_before', opts.createdBefore);
  if (opts.includeGuardians) qs.set('include_guardians', 'true');
  if (opts.includeTeams) qs.set('include_teams', 'true');
  if (opts.costTypeId) qs.set('cost_type_id', opts.costTypeId);
  if (opts.paymentStatus) qs.set('payment_status', opts.paymentStatus);
  if (opts.fiscalYear !== undefined) qs.set('fiscal_year', String(opts.fiscalYear));
  const query = qs.toString();
  const path = `/api/v1/organizations/${encodeURIComponent(slug)}/members${query ? `?${query}` : ''}`;

  return apiFetch<ListMembersResponse>(path, { token });
}

export interface CreateMemberInput {
  first_name: string;
  last_name: string;
  email?: string;
  city?: string;
  phone?: string;
  street_address?: string;
  postal_code?: string;
  // National ID in canonical form. Server re-validates against the org's
  // country regardless of what the SPA sends. Optional.
  personal_id?: string;
}

export async function createMember(
  token: string | null,
  slug: string,
  input: CreateMemberInput,
): Promise<Member> {
  return apiFetch<Member>(`/api/v1/organizations/${encodeURIComponent(slug)}/members`, {
    method: 'POST',
    body: input,
    token,
  });
}

// MemberDetail is the GET /members/{id} response: full row + has_photo
// (true when the server has a photo_object_key set). Photo bytes come
// via a separate /photo URL the SPA puts in <img src>.
export interface MemberDetail {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  city: string;
  phone: string;
  street_address: string;
  postal_code: string;
  personal_id?: string;
  personal_id_status: 'none' | 'valid' | 'needs_review';
  has_guardians: boolean;
  has_photo: boolean;
  created_at: string;
  // birth_date is "YYYY-MM-DD" or null when unknown. Used by the
  // MemberCard's Profile tab to decide whether the photo-consent toggle
  // should be visible (only for under-18 members).
  birth_date: string | null;
  // gender is "male" | "female" | "other" or "" when not recorded. Optional
  // demographic tag used only for aggregate slices (e.g. the pulse report).
  gender: string;
  // guardian_public_consent is the bool form of guardian_public_consent_at
  // — TRUE when a guardian has granted consent for the minor's photo
  // to appear on the public roster.
  guardian_public_consent: boolean;
  // no_email is the admin's "this member has no email" assertion. When
  // true, the members-missing-email assistant check skips the row so the
  // suggestion can be dismissed per-member.
  no_email: boolean;
}

export async function getMember(
  token: string | null,
  slug: string,
  memberID: string,
): Promise<MemberDetail> {
  return apiFetch<MemberDetail>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberID)}`,
    { token },
  );
}

// memberPhotoURL builds the absolute URL for the photo proxy endpoint.
// The cacheBust query param is incremented on upload so the SPA bypasses
// any browser cache for the freshly-changed image. We can't use the
// object key here (the SPA doesn't see it) so the timestamp is the
// simplest safe invalidator.
export function memberPhotoURL(slug: string, memberID: string, cacheBust?: number | string): string {
  const base = `${config.apiURL}/api/v1/organizations/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberID)}/photo`;
  if (cacheBust === undefined) return base;
  return `${base}?v=${encodeURIComponent(String(cacheBust))}`;
}

// uploadMemberPhoto sends a single file via multipart/form-data. Caller is
// responsible for client-side validation (size cap / image MIME) before
// calling — the server re-checks both, so this is just UX nicety.
export async function uploadMemberPhoto(
  token: string | null,
  slug: string,
  memberID: string,
  file: File,
): Promise<{ has_photo: boolean; content_type: string }> {
  const form = new FormData();
  form.append('photo', file);
  // We can't go through apiFetch (it sets Content-Type: application/json).
  // Hand-rolled fetch keeps the multipart boundary intact.
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(
    `${config.apiURL}/api/v1/organizations/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberID)}/photo`,
    { method: 'POST', headers, body: form },
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`upload failed: ${res.status} ${text}`);
  }
  return (await res.json()) as { has_photo: boolean; content_type: string };
}

export async function deleteMemberPhoto(
  token: string | null,
  slug: string,
  memberID: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberID)}/photo`,
    { method: 'DELETE', token },
  );
}

// --- Guardians ---
//
// member_guardian rows attached to a single member. The Members page
// accordion fetches this lazily when an item is opened so we don't
// over-fetch on the list view.

// Closed set mirroring the CHECK constraint on members.member_guardian.
// Empty string = unknown / not yet set (existing rows pre-migration, or
// guardians created via flows that don't capture relationship). SPA never
// renders the raw string — always translates via members.guardians.relationship.*.
export type GuardianRelationship =
  | ''
  | 'parent'
  | 'grandparent'
  | 'sibling'
  | 'other_relative'
  | 'legal_guardian'
  | 'other';

export interface Guardian {
  link_id: string;
  member_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  street_address: string;
  postal_code: string;
  city: string;
  // Same shape as Member's personal_id fields. Drives the warning triangle
  // on the guardian row in the expanded panel.
  personal_id?: string;
  personal_id_status: 'none' | 'valid' | 'needs_review';
  relationship: GuardianRelationship;
  // True when this guardian has a profile photo on file. Drives the avatar
  // bubble on the relations-tab row (photo via memberPhotoURL, initials
  // fallback otherwise).
  has_photo: boolean;
}

export interface ListGuardiansResponse {
  guardians: Guardian[];
}

// listGuardianIDsForFilter fetches the union of guardian member.id values
// for every (non-deleted) member matching the given search filter — same
// icontains rule the members list uses. Used by the Members table header
// dropdown's "Members + guardians" / "Only guardians" actions to operate
// on the full filter result, not just the visible page. Single batched
// server call.
export async function listGuardianIDsForFilter(
  token: string | null,
  slug: string,
  search: string,
): Promise<string[]> {
  const qs = new URLSearchParams();
  if (search) qs.set('search', search);
  const path = `/api/v1/organizations/${encodeURIComponent(slug)}/members/guardian-ids${
    qs.toString() ? `?${qs.toString()}` : ''
  }`;
  const res = await apiFetch<{ guardian_ids: string[] }>(path, { token });
  return res.guardian_ids;
}

export async function listGuardians(
  token: string | null,
  slug: string,
  memberID: string,
): Promise<ListGuardiansResponse> {
  return apiFetch<ListGuardiansResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberID)}/guardians`,
    { token },
  );
}

// Dependent is the inverse of Guardian: a member who has the given member
// listed as a guardian. Same wire shape as Guardian — relationship reads
// from the same member_guardian.relationship column either way.
export interface Dependent {
  link_id: string;
  member_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  street_address: string;
  postal_code: string;
  city: string;
  personal_id?: string;
  personal_id_status: 'none' | 'valid' | 'needs_review';
  relationship: GuardianRelationship;
  // True when this dependent has a profile photo on file. Drives the
  // relations-tab avatar (same as Guardian.has_photo).
  has_photo: boolean;
}

export interface ListDependentsResponse {
  dependents: Dependent[];
}

export async function listDependents(
  token: string | null,
  slug: string,
  memberID: string,
): Promise<ListDependentsResponse> {
  return apiFetch<ListDependentsResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberID)}/dependents`,
    { token },
  );
}

// LinkGuardianInput — wire shape for the existing-member branch of
// POST /members/{id}/guardians. Use this when the user picked an
// existing org member from the search picker.
export interface LinkGuardianInput {
  guardian_member_id: string;
  relationship: GuardianRelationship;
}

// CreateGuardianInput — wire shape for the create-new branch. Server
// inserts the new member AND links it as guardian in one transaction.
// Email + phone + address fields are all optional.
export interface CreateGuardianInput {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  street_address?: string;
  postal_code?: string;
  city?: string;
  personal_id?: string;
  relationship: GuardianRelationship;
}

// AddGuardianResponse covers both branches. `link_only=true` on the
// existing-member path; `link_only=false` on the create-new path with
// the new member's basic fields populated for an immediate UI render.
export interface AddGuardianResponse {
  link_only: boolean;
  guardian_member_id: string;
  relationship: GuardianRelationship;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  has_contact_issue?: boolean;
}

export async function linkGuardian(
  token: string | null,
  slug: string,
  memberID: string,
  input: LinkGuardianInput,
): Promise<AddGuardianResponse> {
  return apiFetch<AddGuardianResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberID)}/guardians`,
    { token, method: 'POST', body: input },
  );
}

export async function createAndLinkGuardian(
  token: string | null,
  slug: string,
  memberID: string,
  input: CreateGuardianInput,
): Promise<AddGuardianResponse> {
  return apiFetch<AddGuardianResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberID)}/guardians`,
    { token, method: 'POST', body: input },
  );
}

export async function unlinkGuardian(
  token: string | null,
  slug: string,
  memberID: string,
  guardianMemberID: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberID)}/guardians/${encodeURIComponent(guardianMemberID)}`,
    { token, method: 'DELETE' },
  );
}

// updateGuardianRelationship changes the relationship label on an existing
// (member, guardian) link. Direct human-as-self write (no CommandIntent) — the
// AI path uses the member_guardian.update_relationship Operation. A no-op
// (relationship unchanged) is accepted server-side and writes nothing.
export async function updateGuardianRelationship(
  token: string | null,
  slug: string,
  memberID: string,
  guardianMemberID: string,
  relationship: GuardianRelationship,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberID)}/guardians/${encodeURIComponent(guardianMemberID)}`,
    { token, method: 'PATCH', body: { relationship } },
  );
}

// --- Team assignments (members.team_member rows for one member) ---

// Mirrors the CHECK constraint on members.team_member.role.
export type MemberTeamRole = 'athlete' | 'leader';

// One row in the MemberCard's Teams tab. assignment_id is the
// team_member.id and is included so future edit flows can target a
// specific row, but the (team_id, role) pair is the logical identity.
//
// jersey_number is `number | null` because NULL is meaningful — it
// means "no jersey assigned" and is different from 0. position is a
// free-text string (empty when not set). public_visibility opts the
// row into the public team roster — schema default is TRUE, admins
// flip to FALSE per (member, team) to hide.
export interface MemberTeamAssignment {
  assignment_id: string;
  team_id: string;
  team_name: string;
  team_color: string;
  role: MemberTeamRole;
  jersey_number: number | null;
  position: string;
  public_visibility: boolean;
}

export interface ListMemberTeamsResponse {
  assignments: MemberTeamAssignment[];
}

export async function listMemberTeams(
  token: string | null,
  slug: string,
  memberID: string,
): Promise<ListMemberTeamsResponse> {
  return apiFetch<ListMemberTeamsResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberID)}/teams`,
    { token },
  );
}

// --- Member import ---
//
// Two endpoints, both server-driven:
//   /members/import/match-columns — given parsed file headers, returns
//     suggested mappings (fuzzy-matched against synonyms in the backend).
//   /members/import — takes canonical rows and creates members + teams +
//     role assignments transactionally.
//
// File parsing (CSV via PapaParse, XLSX via exceljs) happens client-side
// in ImportMembersModal — these wrappers only handle the JSON traffic.

export type MemberImportField =
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'phone'
  | 'street_address'
  | 'postal_code'
  | 'city'
  | 'personal_id'
  | 'team'
  | 'team_role'
  | 'member_roles';

export interface MatchSuggestion {
  column: string;
  field?: MemberImportField | '';
  confidence: number;
}

// RawSheetPayload mirrors the Go RawSheet wire shape. The SPA extracts the
// FULL 2D matrix client-side and forwards only the first ~20 rows of each
// sheet to the analyzer (see PREVIEW_ROWS in memberImport.ts).
export interface RawSheetPayload {
  sheet_name: string;
  rows: string[][];
}

// SheetLayoutPayload mirrors the Go SheetLayout wire shape. The analyzer
// returns one entry per input sheet, in input order — the SPA passes them
// straight into applyLayout to build the keyed ParsedFile.
export interface SheetLayoutPayload {
  sheet_name: string;
  header_row_index: number;
  skip_rows_before_header: number;
  skip_row_pattern: string;
  // Mirror of the Go field; see SheetLayout in lib/memberImport.ts for the
  // semantics. Kept optional in the wire shape because the AI only sets it
  // when it detects the SportAdmin Se "Till målsman för:" column-A pattern.
  guardian_marker_prefix?: string;
  notes?: string;
}

interface MatchColumnsResponse {
  layouts?: SheetLayoutPayload[];
  suggestions: MatchSuggestion[];
  source: 'ai' | 'fuzzy';
}

export interface MatchColumnsResult {
  layouts: SheetLayoutPayload[];
  suggestions: MatchSuggestion[];
  source: 'ai' | 'fuzzy';
}

// matchColumns sends the raw sheet preview to /import/match-columns and
// receives layouts + column mappings in a single round-trip. The backend
// uses Haiku to identify the header row, decorative leading rows, and any
// interleaved label-row prefix BEFORE mapping headers to canonical fields
// — so the SPA no longer needs to assume row 0 holds the headers.
export async function matchColumns(
  token: string | null,
  rawSheets: RawSheetPayload[],
): Promise<MatchColumnsResult> {
  const res = await apiFetch<MatchColumnsResponse>(
    '/api/v1/members/import/match-columns',
    { method: 'POST', body: { raw_sheets: rawSheets }, token },
  );
  return {
    layouts: res.layouts ?? [],
    suggestions: res.suggestions,
    source: res.source,
  };
}

export interface ImportRow {
  first_name: string;
  last_name: string;
  email?: string;
  city?: string;
  phone?: string;
  street_address?: string;
  postal_code?: string;
  // National ID in raw form (server runs the country validator). Invalid
  // values are stored verbatim with status='needs_review' so nothing in the
  // source file is lost.
  personal_id?: string;
  team?: string;
  team_role?: string;
  member_roles?: string[];
  // guardian_of_name: when set, names a subject (by full "<first> <last>"
  // string) for whom THIS row's person is a guardian. Populated by the SPA
  // when a sheet's layout carried guardian_marker_prefix and column A read
  // "Till målsman för: <subject>". The server resolves the name to a member
  // within the same import batch and inserts a members.member_guardian row;
  // ambiguous / not-found pointers land in ImportResult.skipped.
  guardian_of_name?: string;
}

// --- Role-value translation (AI-assisted) ---
//
// After column mapping is confirmed, the SPA collects the unique role values
// from the role columns and asks the server to classify each into a canonical
// {axis, code} pair. The server hits its static synonym table first, then
// asks Bedrock for the residue. This is what lets the importer accept
// "Materialförvaltare" or "Pojklagsledare" without us cataloging every
// possible Swedish title up front.

export interface ValueTranslation {
  value: string;
  code: string; // canonical code, or "" if unknown
  axis: 'team_role' | 'member_role' | '';
  source: 'static' | 'ai' | 'unknown';
  notes?: string;
}

interface TranslateValuesResponse {
  translations: ValueTranslation[];
}

export async function translateRoleValues(
  token: string | null,
  values: string[],
): Promise<ValueTranslation[]> {
  if (values.length === 0) return [];
  const res = await apiFetch<TranslateValuesResponse>(
    '/api/v1/members/import/translate-values',
    { method: 'POST', body: { values }, token },
  );
  return res.translations;
}

export interface ImportSkipped {
  row_index: number;
  reason: string;
  value?: string;
}

export interface ImportResult {
  imported: number;
  merged: number;
  teams_created: number;
  // guardian_links counts new dependent→guardian rows written by the
  // Förälder→subject lookback during import. Idempotent re-runs report 0.
  guardian_links: number;
  skipped: ImportSkipped[];
}

// Response shape when the server queues an import and returns 202 Accepted.
// The actual import runs asynchronously; the user sees the result via the
// notification bell when it completes.
export interface ImportAccepted {
  // Old endpoint returned 'queued'; new queued endpoint returns the
  // bulk_import_job's status ('pending' on enqueue). Accept either.
  status: 'queued' | 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  row_count: number;
  // Present on the new endpoint; absent on the legacy goroutine path.
  job_id?: string;
  created_at?: string;
}

export interface ImportValidationError {
  row_index: number;
  field: string;
  reason: string;
}

// ImportRejected is what /members/import returns on a 400 with validation
// errors: same shape as a regular ApiError but with the per-row detail list
// the SPA needs to highlight cells.
export interface ImportRejected {
  error: 'validation_failed';
  validation_errors: ImportValidationError[];
}

// Server enqueues the import into members.bulk_import_job and returns 202
// + job_id. The worker (cmd/worker) picks it up within ~5s. Survives an
// API redeploy mid-run — unlike the old in-process goroutine path, which
// silently dropped work on every Kamal restart.
//
// Old endpoint (/members/import) is still served by the API during this
// transition and removed in a follow-up PR; this client function points
// at the queued path going forward.
export async function bulkImport(
  token: string | null,
  slug: string,
  rows: ImportRow[],
): Promise<ImportAccepted> {
  return apiFetch<ImportAccepted>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/members/import/queue`,
    { method: 'POST', body: { rows }, token },
  );
}

// --- Preflight validation ---
//
// Runs the same row checks bulkImport would, but with no DB writes and no
// org context required. Used by the onboarding wizard so the user sees
// fixable issues (missing first/last name, dropped role values) BEFORE we
// commit to creating the organization.

export interface ImportPreflight {
  validation_errors: ImportValidationError[]; // hard errors that block import
  would_skip: ImportSkipped[];                // role values that would be dropped
  row_count: number;
}

export async function validateImport(
  token: string | null,
  rows: ImportRow[],
  // Country code drives the national-ID validator. Onboarding has the user's
  // chosen country in hand before the org is created; pass it here so
  // preflight catches bad personnummer up front. Empty disables checksum
  // validation (raw values pass through with would_skip='needs_review').
  countryCode = '',
): Promise<ImportPreflight> {
  return apiFetch<ImportPreflight>('/api/v1/members/import/validate', {
    method: 'POST',
    body: { rows, country_code: countryCode },
    token,
  });
}
